/**
 * I18n Context & Provider
 * 
 * Manages language state and translations throughout the app
 */

import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { TranslationManager } from '../i18n';
import {
  SUPPORTED_LANGUAGES,
  getInitialLanguage,
  storeLanguagePreference,
  DEFAULT_LANGUAGE,
} from '../i18n/config';

export interface I18nContextType {
  t: (key: string, params?: Record<string, string | number>) => string;
  language: string;
  changeLanguage: (languageCode: string) => Promise<void>;
  direction: 'ltr' | 'rtl';
  isRTL: boolean;
}

export const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const [translationManager] = useState(() => new TranslationManager());
  const [language, setLanguage] = useState<string>(getInitialLanguage());
  const [isReady, setIsReady] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(0); // Force re-render when translations change

  // Get language info
  const languageInfo = SUPPORTED_LANGUAGES[language] || SUPPORTED_LANGUAGES[DEFAULT_LANGUAGE];
  const direction = languageInfo.direction;
  const isRTL = direction === 'rtl';

  /**
   * Initialize translations on mount
   */
  useEffect(() => {
    const initTranslations = async () => {
      try {
        await translationManager.loadLanguage(language);
        setIsReady(true);
      } catch (error) {
        console.error('Failed to initialize translations:', error);
        setIsReady(true); // Still mark as ready to prevent blocking
      }
    };

    initTranslations();
  }, [translationManager, language]);

  /**
   * Update document direction when language changes
   */
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir', direction);
      document.documentElement.setAttribute('lang', language);
    }
  }, [direction, language]);

  /**
   * Change language
   */
  const changeLanguage = useCallback(
    async (languageCode: string) => {
      if (!SUPPORTED_LANGUAGES[languageCode]) {
        console.warn(`Unsupported language: ${languageCode}`);
        return;
      }

      console.log(`[I18n] Changing language to: ${languageCode}`);
      
      try {
        await translationManager.loadLanguage(languageCode);
        console.log(`[I18n] Translations loaded for: ${languageCode}`);
        console.log(`[I18n] Test translation: ${translationManager.t('language.selectLanguage')}`);
        
        setLanguage(languageCode);
        storeLanguagePreference(languageCode);
        setUpdateTrigger(prev => {
          const newValue = prev + 1;
          console.log(`[I18n] Update trigger: ${prev} -> ${newValue}`);
          return newValue;
        }); // Force re-render of all components using translations

        // Sync with backend if user is authenticated
        try {
          const token = localStorage.getItem('token');
          if (token) {
            await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/language`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ preferredLanguage: languageCode }),
            });
          }
        } catch (error) {
          // Non-critical: language still changed locally
          console.warn('Failed to sync language preference to server:', error);
        }
      } catch (error) {
        console.error(`Failed to change language to ${languageCode}:`, error);
      }
    },
    [translationManager]
  );

  /**
   * Translation function
   * Note: Must depend on language to trigger re-renders when language changes
   */
  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const result = translationManager.t(key, params);
      // Debug logging (remove in production)
      if (process.env.NODE_ENV === 'development' && key === 'language.selectLanguage') {
        console.log(`[I18n] t('${key}') = '${result}' (language: ${language}, trigger: ${updateTrigger})`);
      }
      return result;
    },
    [translationManager, language, updateTrigger] // Add language and updateTrigger dependency to trigger re-renders
  );

  // Create context value - use useMemo to ensure it updates when language changes
  // MUST be called before any early returns to satisfy React hooks rules
  // Note: updateTrigger is deliberately included in t's dependencies but not here
  // to prevent unnecessary context re-creation while still forcing component updates
  const value: I18nContextType = React.useMemo(() => ({
    t,
    language,
    changeLanguage,
    direction,
    isRTL,
  }), [t, language, changeLanguage, direction, isRTL]); // Removed updateTrigger from context deps

  // Don't render children until translations are loaded
  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

